import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Toast, ToastContainer } from "react-bootstrap";
import "./AppToast.css";

/**
 * Testing: when `true`, every toast stays open until the user closes it (no timer).
 * Set to `false` for normal auto-hide behavior.
 */
const APP_TOAST_TESTING_NEVER_AUTO_CLOSE = false;

/**
 * Optional: `REACT_APP_TOAST_FORCE_MANUAL=true` — same effect as always manual (env override).
 */
const FORCE_MANUAL_ALL =
  typeof process !== "undefined" &&
  String(process.env.REACT_APP_TOAST_FORCE_MANUAL || "").toLowerCase() === "true";

/** Set to `false` to stop console diagnostics (or gate on env when no longer needed). */
const APP_TOAST_DEBUG = true;

function debugAppToast(label, payload) {
  if (!APP_TOAST_DEBUG) return;
  // eslint-disable-next-line no-console
  console.log(`[AppToast:debug] ${label}`, payload);
}

/** Default auto-hide (ms) when `delay` is omitted and not `sticky`/manual. */
export const APP_TOAST_DEFAULT_DELAY = {
  success: 3000,
  danger: 5000,
  warning: 4000,
  info: 4000,
};

/**
 * Map API / legacy `type` and `variant` to a Bootstrap Toast `bg` value.
 * Callers may use `variant: 'danger'` or `type: 'error'` interchangeably.
 */
export function normalizeAppToastVariant(type, variant) {
  const raw = type != null && type !== "" ? type : variant;
  const s = String(raw || "success").toLowerCase().trim();
  if (s === "error") return "danger";
  return s;
}

function coerceSticky(sticky) {
  return (
    sticky === true ||
    sticky === 1 ||
    String(sticky).toLowerCase() === "true"
  );
}

const noop = () => {};

/**
 * In-app toast (portal). Auto-hide is implemented here — not via react-bootstrap's
 * `autohide`/`delay`, so sticky/manual toasts are never closed by an internal timer.
 */
export default function AppToast({
  show,
  message,
  variant = "success",
  type,
  onClose,
  sticky = false,
  delay,
  messageFontSize,
}) {
  const effectiveVariant = normalizeAppToastVariant(type, variant);
  const lightBackground =
    effectiveVariant === "warning" ||
    effectiveVariant === "info" ||
    effectiveVariant === "light";
  const bodyTextClass = lightBackground ? "text-dark" : "text-white";

  const stickyOn = coerceSticky(sticky);
  /** Explicit 0 / false = stay until user dismisses */
  const manualDismiss =
    APP_TOAST_TESTING_NEVER_AUTO_CLOSE ||
    FORCE_MANUAL_ALL ||
    stickyOn ||
    delay === 0 ||
    delay === false;

  const numericDelay =
    delay != null && typeof delay === "number" && Number.isFinite(delay) && delay > 0
      ? delay
      : null;

  const autoHideMs = manualDismiss
    ? null
    : numericDelay != null
      ? numericDelay
      : APP_TOAST_DEFAULT_DELAY[effectiveVariant] ?? 3000;

  useEffect(() => {
    if (!show || !APP_TOAST_DEBUG) return;
    debugAppToast("visible", {
      rawProps: {
        variant,
        type,
        sticky,
        delay,
        messageFontSize,
        messagePreview:
          typeof message === "string"
            ? `${message.slice(0, 100)}${message.length > 100 ? "…" : ""}`
            : String(message).slice(0, 100),
      },
      derived: {
        effectiveVariant,
        stickyOn,
        manualDismiss,
        numericDelay,
        autoHideMs,
        showCloseButtonWillRender: manualDismiss,
      },
      flags: {
        APP_TOAST_TESTING_NEVER_AUTO_CLOSE,
        FORCE_MANUAL_ALL,
      },
    });
  }, [
    show,
    message,
    variant,
    type,
    sticky,
    delay,
    messageFontSize,
    effectiveVariant,
    stickyOn,
    manualDismiss,
    numericDelay,
    autoHideMs,
  ]);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!show || manualDismiss || autoHideMs == null) return;
    const id = window.setTimeout(() => {
      onCloseRef.current?.();
    }, autoHideMs);
    return () => window.clearTimeout(id);
  }, [show, manualDismiss, autoHideMs, message]);

  const showCloseButton = manualDismiss;

  const toastContent = (
    <ToastContainer
      position="top-center"
      className="p-3 app-toast-container"
      style={{
        zIndex: 99999,
        position: "fixed",
        top: "20px",
        left: "50%",
        transform: "translateX(-50%)",
        pointerEvents: "none",
      }}
    >
      <Toast
        /* RB's autohide timer must not run — we own auto-hide via useEffect above. */
        onClose={noop}
        show={show}
        delay={0}
        autohide={false}
        bg={effectiveVariant}
        style={{
          minWidth: "450px",
          fontSize: "16px",
          fontWeight: "500",
          pointerEvents: "auto",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          border: effectiveVariant === "danger" ? "2px solid #dc3545" : "none",
          ...(messageFontSize
            ? { ["--app-toast-message-font-size"]: messageFontSize }
            : {}),
        }}
      >
        <Toast.Body
          className={`${bodyTextClass} d-flex align-items-center justify-content-between`}
          style={{ padding: "15px 20px" }}
        >
          <div className="d-flex align-items-center">
            <i
              className={`fas fa-${
                effectiveVariant === "success"
                  ? "check-circle"
                  : effectiveVariant === "danger"
                    ? "exclamation-triangle"
                    : effectiveVariant === "warning"
                      ? "exclamation-circle"
                      : "info-circle"
              } me-3`}
              aria-hidden
            />
            <span className={messageFontSize ? "app-toast-message-text" : undefined}>
              {message}
            </span>
          </div>
          {showCloseButton && (
            <button
              type="button"
              className={`app-toast-dismiss d-flex align-items-center justify-content-center border-0 rounded ${
                lightBackground ? "app-toast-dismiss--light" : "app-toast-dismiss--dark"
              }`}
              onClick={() => onCloseRef.current?.()}
              aria-label="Close"
            >
              {/* Inline SVG: avoids FA + Bootstrap text color on bg-* washing out the icon */}
              <svg
                className="app-toast-dismiss-icon"
                width="18"
                height="18"
                viewBox="0 0 16 16"
                aria-hidden
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"
                />
              </svg>
            </button>
          )}
        </Toast.Body>
      </Toast>
    </ToastContainer>
  );
  return createPortal(toastContent, document.body);
}
